import { Injectable } from '@angular/core';

import { GenericAnalyticsBrandDimensions } from './config/config.models';
import {
  AnalyticsTrackEvent
} from './models';
import { GoogleTagManagerService } from './google-tag-manager/google-tag-manager.service';
import { AnalyticsHelpers, genericAnalyticObject } from './helpers/analytics.helpers';
import { AnalyticsConfig, AnalyticsEcommerceGTMSettings, AnalyticsSettings, EventFunction } from './models/analytics-data.model';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService<GenericContext, GenericComponentData> {

  private events: Record<
    string,
    EventFunction<GenericContext, GenericComponentData> | undefined
  > = {};

  private helpers: GenericAnalyticsBrandDimensions<GenericContext, GenericComponentData> = {};
  private enableAnalytics: boolean = false;
  private eventsToTrack: AnalyticsTrackEvent<GenericComponentData>[] = [];
  private analyticsConfigLoaded: boolean = false;
  private gtmId?: string;

  private context!: GenericContext;

  constructor(private readonly gtmService: GoogleTagManagerService) {}

  public updateConfig({
    cmsConfig,
    context,
    brandSettings,
    brandDimensions,
    allowMultipleGtmScripts
  }: AnalyticsConfig<GenericContext, GenericComponentData>): void {
    if (isDefined(cmsConfig)) {
      if (isDefined(cmsConfig.gtmId)) {
        this.enableAnalytics = !!cmsConfig.enableAnalyticsRender;
        this.gtmId = cmsConfig.gtmId;
      }
    }
    if (isDefined(context)) {
      this.context = context;
    }
    if (isDefined(brandSettings) || isDefined(brandDimensions)) {
      this.extendEventsAndHelpers({
        brandSettings,
        brandDimensions
      });
    }
    if (allowMultipleGtmScripts) {
      this.gtmService.setAllowMultipleScripts(allowMultipleGtmScripts);
    }
    if (this.enableAnalytics && this.context) {
      this.gtmService.setGtmId(this.gtmId!);
      this.analyticsConfigLoaded = true;
      this.emptyQueue();
    }
  }

  public track(event: string, settings: GenericComponentData): void {
    if (!this.analyticsConfigLoaded) {
      this.eventsToTrack.push({ name: event, data: settings });
      return;
    }
    const eventToCall = this.events[event];
    if (isDefined(eventToCall) && this.enableAnalytics) {
      const settingsGTM: AnalyticsEcommerceGTMSettings | undefined = eventToCall({
        componentData: settings,
        context: this.context,
        helpers: this.helpers
      });
      if (settingsGTM) {
        const parsedSettingsGTM = AnalyticsHelpers.parseObject(
          settingsGTM as unknown as genericAnalyticObject
        );
        this.gtmService.pushTag(parsedSettingsGTM as unknown as AnalyticsEcommerceGTMSettings);
      }
    }
  }

  private extendEventsAndHelpers(
    analytics: AnalyticsSettings<GenericContext, GenericComponentData>
  ): void {
    this.events = {
      ...this.events,
      ...analytics.brandSettings
    };
    this.helpers = {
      ...this.helpers,
      ...analytics.brandDimensions
    };
  }

  private emptyQueue(): void {
    if (this.eventsToTrack.length === 0) {
      return;
    }
    const untrackedEvents: AnalyticsTrackEvent<GenericComponentData>[] = [];
    this.eventsToTrack.forEach((event) => {
      if (this.events[event.name]) {
        this.track(event.name, event.data);
      } else {
        untrackedEvents.push(event);
      }
    });
    this.eventsToTrack = untrackedEvents;
  }

}
